import React from 'react';
import memoizeOne from 'memoize-one';
import gql from 'graphql-tag';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient.ts';
import { requireStyleClassResourcesForTemplates } from '@confluence/template-utils/entry-points/requireStyleClassResourcesForTemplates.ts';
import { getTemplateId } from '@confluence/template-utils/entry-points/templateUtils.ts';
import { TemplateIcon } from '@confluence/template-icon/entry-points/TemplateIcon.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import type { ConfluencePageContext, Option } from '../extensionTypes.ts';

enum SortOrder {
	RELEVANCY_SORT = 'web.item.sorting.scheme.relevancy',
}

const AllTemplatesQuery =
	// eslint-disable-next-line graphql-relay-compat/consistent-graphql-operation-naming -- Read https://go/connie-relay-migration-fyi
	gql`
		query AllTemplatesQuery($spaceKey: String!, $sortingScheme: String!) {
			allTemplates(spaceKey: $spaceKey, sortingScheme: $sortingScheme) {
				nodes {
					name
					description
					templateId
					contentBlueprintId
					iconURL
					darkModeIconURL
				}
			}
		}
	`;

const loadLegacyTemplateStyles = memoizeOne(requireStyleClassResourcesForTemplates);

const getAllTemplates = async (spaceKey: string): Promise<any> => {
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const allTemplatesData = await getApolloClient().query({
		query: AllTemplatesQuery,
		variables: { spaceKey, sortingScheme: SortOrder.RELEVANCY_SORT },
	});

	return allTemplatesData?.data.allTemplates?.nodes;
};

const search = async (spaceKey: string): Promise<Option[]> => {
	try {
		const templates = await getAllTemplates(spaceKey);

		return templates.map((template) => ({
			label: template.name,
			description: template.description,
			icon: <TemplateIcon size="small" template={template} />,
			value: getTemplateId(template),
		}));
	} catch (error) {
		getMonitoringClient().submitError(
			new Error(
				`Failed to fetch templates from template resolver in macro config panel with error: ${error}`,
			),
			{
				attribution: Attribution.PAGE_MODES,
			},
		);

		return [];
	}
};

export const templateResolverFor = (spaceKey?: string) => async (_searchTerm, _selectedValue) => {
	return spaceKey ? search(spaceKey) : [];
};

export const getTemplateResolver = ({ spaceKey }: ConfluencePageContext) => {
	loadLegacyTemplateStyles();
	return templateResolverFor(spaceKey);
};
